import { useState, useEffect } from "react";
import { getCommercialInspectionForm } from "../services/inspectionFormService";
import { getErrorMessage } from "../utils/api/errorUtils";
import {
  DrawData,
  InspectionFormApiResponse,
  InspectorForm,
  Photo,
  Project,
  SovItem,
} from "../Pages/Inspector/InspectorFi/types/inspector.types";
import {
  formatCurrency,
  mapDrawLienItem,
  mapNotDrawnItem,
} from "../Pages/Inspector/InspectorFi/utils/inspectorHelpers";

export interface MappedInspectionData {
  project: Project;
  sovItems: SovItem[];
  notDrawnItems: [string, string, string][];
  photos: Photo[];
  inspectorForm: InspectorForm;
  generalNotes: string;
  flagNotes: string;
  offsiteNotes: string;
  drawData: DrawData;
  drawAmount: string;
  drawPercentage: string;
}

interface ApiResponseWrapper {
  data?: InspectionFormApiResponse;
}

interface ApiError {
  message?: string;
  raw?: { fields?: Record<string, string> };
}

const buildDrawData = (
  d: InspectionFormApiResponse,
  sym: string,
): DrawData => ({
  applicationNumber: String(d.drawNumber ?? ""),
  projectName: d.project?.name ?? "",
  period: "",
  currentPaymentDue: formatCurrency(d.g702Data?.currentPaymentDue?.amount, sym),
  currencySymbol: sym,
  rows: [
    {
      num: "1",
      label: "Original contract sum",
      val: formatCurrency(d.g702Data?.originalContractSum?.amount, sym),
    },
    // {
    //   num: "1a",
    //   label: "SOV Update / Final Contract",
    //   val: formatCurrency(d.g702Data?.sovUpdate?.amount, sym),
    // },
    {
      num: "2",
      label: "Net change by change orders",
      val: formatCurrency(d.g702Data?.netChangeByChangeOrders?.amount, sym),
    },
    {
      num: "3",
      label: "Contract sum to date",
      val: formatCurrency(d.g702Data?.contractSumToDate?.amount, sym),
      color: "blue",
    },
    {
      num: "4",
      label: "Total completed & stored to date",
      val: formatCurrency(d.g702Data?.totalCompletedAndStored?.amount, sym),
    },
    {
      num: "5",
      label: `Total retainage${d.g702Data?.retainageRate ? " (" + parseFloat(String(d.g702Data?.retainageRate)) + "%)" : ""}`,
      val: formatCurrency(d.g702Data?.totalRetainage?.amount, sym),
    },
    {
      num: "6",
      label: "Total earned less retainage",
      val: formatCurrency(d.g702Data?.totalEarnedLessRetainage?.amount, sym),
    },
    {
      num: "7",
      label: "Less previous certificates",
      val: formatCurrency(d.g702Data?.lessPreviousCertificates?.amount, sym),
    },
    {
      num: "8",
      label: "Current payment due",
      val: formatCurrency(d.g702Data?.currentPaymentDue?.amount, sym),
      color: "green",
      isTotal: true,
    },
    {
      num: "9",
      label: "Balance to finish incl. retainage",
      val: formatCurrency(d.g702Data?.balanceToFinish?.amount, sym),
      color: "amber",
    },
  ],
});

const mapResponse = (
  d: InspectionFormApiResponse,
  projectId: string,
  drawId: string,
): MappedInspectionData => {
  const sym = d.g702Data?.originalContractSum?.currency?.symbol ?? "$";
  const bankName =
    d.lender?.bankName ?? d.lender?.companyName ?? d.lender?.branchName ?? "";
  const drawNum = String(d.drawNumber ?? "");
  const projectType = d.project?.type ?? "";

  const project: Project = {
    id: projectId,
    drawId,
    name: d.project?.name ?? "",
    draw: drawNum ? `Draw #${drawNum}` : "",
    inspection: drawNum,
    bank: bankName,
    type: projectType.toUpperCase(),
    address: d.project?.address ?? "",
    sub: [drawNum && `Draw #${drawNum}`, `Inspection #${drawNum}`, bankName]
      .filter(Boolean)
      .join(" · "),
    meta: [bankName, projectType, drawNum && `Draw #${drawNum} pending`]
      .filter(Boolean)
      .join(" · "),
  };

  return {
    project,
    sovItems: (d.drawLienItems ?? []).map((item) => mapDrawLienItem(item, sym)),
    notDrawnItems: (d.sovLienItemsNotInDraw ?? []).map(mapNotDrawnItem),
    photos: (d.inspection?.documents ?? []).map(
      (doc): Photo => ({
        id: doc.id,
        previewUrl: doc.document.key,
        caption: doc.description ?? "",
        uploadType: doc.type,
        fileName: doc.document.fileName,
        s3Key: doc.document.key,
      }),
    ),
    inspectorForm: {
      name: d.inspection?.inspectorName ?? "",
      number: String(
        d.inspectionNumber ??
          d.inspection?.inspectionNumber ??
          d.drawNumber ??
          "",
      ),
      date: d.inspection?.inspectionDate ?? "",
      estimate: String(d.inspection?.cscEstimation ?? ""),
      bank: bankName,
    },
    generalNotes: d.inspection?.note?.general ?? "",
    flagNotes: d.inspection?.note?.flags ?? "",
    offsiteNotes: d.inspection?.note?.offsite ?? "",
    drawData: buildDrawData(d, sym),
    drawAmount: formatCurrency(d.drawAmount, sym),
    drawPercentage: d.drawPercentage != null ? `${d.drawPercentage}%` : "",
  };
};

const useInspectionCommercialForm = (projectId: string, drawId: string) => {
  const [inspectionData, setInspectionData] =
    useState<MappedInspectionData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!projectId || !drawId) return;

    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError("");

      const { data: raw, error: apiError } = (await getCommercialInspectionForm(
        projectId,
        drawId,
      )) as { data: ApiResponseWrapper | null; error: ApiError | null };

      if (cancelled) return;
      setLoading(false);

      if (apiError) {
        setError(getErrorMessage(apiError, "Failed to load inspection form."));
        return;
      }

      const d = raw?.data as InspectionFormApiResponse | undefined;
      if (!d) return;

      setInspectionData(mapResponse(d, projectId, drawId));
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [projectId, drawId]);

  return { inspectionData, loading, error };
};

export default useInspectionCommercialForm;
